import React, { useState } from 'react';
import { useMutation, useLazyQuery } from '@apollo/react-hooks';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

import './add-address-form-style.scss';
import { useSnackbar } from '@src/providers/SnackbarProvider';

import Button from '@src/componentsNew/Buttons/Button';

// Mutation
import { CUSTOMER_ADDRESS_CREATE } from '@organisms/CheckoutInformation/queries';
import {
  customerAddressCreate,
  customerAddressCreateVariables,
} from '@organisms/CheckoutInformation/__generated__/customerAddressCreate';
import { PINCODE } from '@organisms/ProductPincodeCheck/queries';
import { pincode } from '@organisms/ProductPincodeCheck/__generated__/pincode';

import {
  ADDRESS_NICKNAME_REGEX,
  ALPHABET_ONLY_REGEX,
  MOBILE_NUMBER_REGEX,
  PIN_CODE_REGEX,
  STREET_ADDRESS_REGEX,
  DISALLOWED_ADDRESS_NICKNAME_REGEX,
  DISALLOWED_ALPHABET_ONLY_REGEX,
  DISALLOWED_NUMBER_REGEX,
  DISALLOWED_STREET_ADDRESS_REGEX,
  COUNTRY_AREA_LIST,
} from '@src/utils/constants';

interface IProps {
  createAddressCallBackFunc?: (id: string) => void;
}

const AddAddressForm = ({ createAddressCallBackFunc }: IProps) => {
  const { showSnackbar } = useSnackbar();

  const [pinCodeError, setPinCodeError] = useState(false);

  const validationSchema = Yup.object({
    addressNickname: Yup.string()
      .matches(ADDRESS_NICKNAME_REGEX, "Alphabet, number and '-', '_' characters allowed")
      .max(15, 'Nickname must be less than 15 characters'),
    firstName: Yup.string().required('Enter First name').matches(ALPHABET_ONLY_REGEX, 'Alphabet only'),
    lastName: Yup.string().required('Enter Last name').matches(ALPHABET_ONLY_REGEX, 'Alphabet only'),
    phone1: Yup.string()
      .required('Enter Mobile number')
      .matches(MOBILE_NUMBER_REGEX, 'Please enter a valid mobile number (10 digit)'),
    postalCode: Yup.string()
      .required('Enter Pin Code')
      .matches(PIN_CODE_REGEX, 'Please enter a valid Pin Code (6 digit)'),
    countryArea: Yup.string().required('Select Country Area'),
    city: Yup.string().required('Enter City').matches(ALPHABET_ONLY_REGEX, 'Invalid character included'),
    streetAddress1: Yup.string()
      .required('Enter Street Address')
      .matches(STREET_ADDRESS_REGEX, 'Invalid character included'),
    streetAddress2: Yup.string().required('Required Field').matches(STREET_ADDRESS_REGEX, 'Invalid character included'),
  });

  // Form
  const formik = useFormik({
    initialValues: {
      addressNickname: '',
      firstName: '',
      lastName: '',
      postalCode: '',
      country: 'IN',
      countryArea: 'Delhi',
      city: '',
      streetAddress1: '',
      streetAddress2: '',
      phone0: '+91',
      phone1: '',
    },
    validationSchema: validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      formik.validateForm().then((errors) => {
        if (Object.keys(errors).length === 0) {
          if (pinCodeError) {
            showSnackbar({ message: 'Please check your pin code' });
            return;
          }

          const variables: customerAddressCreateVariables = {
            input: {
              ...values,
              addressNickname: values?.addressNickname?.trim(),
              firstName: values?.firstName?.trim(),
              lastName: values?.lastName?.trim(),
              city: values?.city?.trim(),
              streetAddress1: values?.streetAddress1?.trim(),
              streetAddress2: values?.streetAddress2?.trim(),
            },
          };

          createCustomerAddress({ variables });
        }
      });
    },
  });

  // Mutation
  const [createCustomerAddress, { data, loading }] = useMutation<customerAddressCreate>(CUSTOMER_ADDRESS_CREATE, {
    onCompleted: (data) => {
      if (createAddressCallBackFunc) {
        const addressID = data?.customerAddressCreate?.address?.id;

        createAddressCallBackFunc(addressID);
      }
    },
  });

  // Query
  const [fetchPinCode, { loading: pinCodeLoading }] = useLazyQuery<pincode>(PINCODE, {
    onError: (error) => {
      setPinCodeError(true);
      showSnackbar({ message: 'We can not find your address' });
    },
    onCompleted: (data) => {
      setPinCodeError(false);
      formik.setFieldValue('countryArea', data?.pincode?.countryArea || 'Delhi', false);
      formik.setFieldValue('city', data?.pincode?.city || '', false);
    },
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    let newValue = value;

    switch (name) {
      case 'addressNickname':
        newValue = value.replace(DISALLOWED_ADDRESS_NICKNAME_REGEX, '');
        break;
      case 'firstName':
      case 'lastName':
        newValue = value.replace(DISALLOWED_ALPHABET_ONLY_REGEX, '');
        break;
      case 'phone1':
      case 'postalCode':
        newValue = value.replace(DISALLOWED_NUMBER_REGEX, '');
        break;
      case 'city':
        newValue = value.replace(DISALLOWED_ALPHABET_ONLY_REGEX, '');
        break;
      case 'streetAddress1':
      case 'streetAddress2':
        newValue = value.replace(DISALLOWED_STREET_ADDRESS_REGEX, '');
        break;
    }

    formik.setFieldValue(name, newValue);

    if (name === 'postalCode' && newValue.length === 6) {
      fetchPinCode({ variables: { pincode: newValue } });
    }
  };

  return (
    <form id="add-address-form" onSubmit={formik?.handleSubmit}>
      <TextField
        type="text"
        size="medium"
        className="form-input nickname"
        label="Nickname (Optional)"
        name="addressNickname"
        value={formik?.values?.addressNickname}
        onChange={handleInputChange}
        error={!!formik.errors.addressNickname}
        helperText={formik.errors.addressNickname}
        fullWidth
      />

      <div className="name-input-wrapper">
        <TextField
          type="text"
          size="medium"
          className={`form-input first-name ${formik.errors.firstName || formik.errors.lastName ? 'err' : ''}`}
          label="First Name"
          name="firstName"
          value={formik?.values?.firstName}
          onChange={handleInputChange}
          error={!!formik.errors.firstName}
          helperText={formik.errors.firstName}
          fullWidth
        />
        <TextField
          type="text"
          size="medium"
          className={`form-input last-name ${formik.errors.firstName || formik.errors.lastName ? 'err' : ''}`}
          label="Last Name"
          name="lastName"
          value={formik?.values?.lastName}
          onChange={handleInputChange}
          error={!!formik.errors.lastName}
          helperText={formik.errors.lastName}
          fullWidth
        />
      </div>

      {/* Mobile Number */}
      <TextField
        type="text"
        size="medium"
        className="form-input mobile-number"
        label="Mobile Number"
        name="phone1"
        value={formik?.values?.phone1}
        onChange={handleInputChange}
        error={!!formik.errors.phone1}
        helperText={formik.errors.phone1}
        inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
        fullWidth
      />

      {/* Pin Code */}
      <TextField
        type="text"
        size="medium"
        className="form-input postalCode"
        label="Pin Code"
        name="postalCode"
        value={formik?.values?.postalCode}
        onChange={handleInputChange}
        error={!!formik.errors.postalCode}
        helperText={formik.errors.postalCode}
        inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
        fullWidth
      />

      {/* Country Area */}
      <FormControl className="form-input" fullWidth variant="outlined">
        <InputLabel>State</InputLabel>
        <Select
          className="select-input countryArea"
          label="State"
          name="countryArea"
          value={formik?.values?.countryArea}
          onChange={formik?.handleChange}
          error={!!formik.errors.countryArea}
          fullWidth
        >
          {COUNTRY_AREA_LIST?.map((countryAreaItem) => (
            <MenuItem key={countryAreaItem.value} value={countryAreaItem.value}>
              {countryAreaItem.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* City */}
      <TextField
        type="text"
        size="medium"
        className="form-input city"
        label="City"
        name="city"
        value={formik?.values?.city}
        onChange={handleInputChange}
        error={!!formik.errors.city}
        helperText={formik.errors.city}
        fullWidth
      />

      {/* Street Address1 */}
      <TextField
        type="text"
        size="medium"
        className="form-input streetAddress1"
        label="Street Address"
        name="streetAddress1"
        value={formik?.values?.streetAddress1}
        onChange={handleInputChange}
        error={!!formik.errors.streetAddress1}
        helperText={formik.errors.streetAddress1}
        fullWidth
      />

      {/* Street Address2 */}
      <TextField
        type="text"
        size="medium"
        className="form-input streetAddress2"
        label="Apartment, Suite, Unit, Building..."
        name="streetAddress2"
        value={formik?.values?.streetAddress2}
        onChange={handleInputChange}
        error={!!formik.errors.streetAddress2}
        helperText={formik.errors.streetAddress2}
        fullWidth
      />

      {/* Submit Button*/}
      <Button
        type="submit"
        size="large"
        cName={`submit-btn ${loading || pinCodeLoading || !formik?.dirty ? 'disable' : ''}`}
        variant="contained"
        disabled={loading || pinCodeLoading || !formik?.dirty}
      >
        Add Address
      </Button>
    </form>
  );
};

export default AddAddressForm;
